import { useEffect, useState } from 'react';

import { fileUploadService } from '@/api/fileUploadApi';
import { useUserId } from '@/auth/UserIdContext';

export function isAzureBlobUrl(url: string | null | undefined): boolean {
	if (!url) return false;
	try {
		const parsed = new URL(url);
		return (
			parsed.protocol === 'https:' &&
			parsed.hostname.endsWith('.blob.core.windows.net')
		);
	} catch {
		return false;
	}
}

export function stripBlobUrlQuery(url: string): string {
	if (!isAzureBlobUrl(url)) return url;
	const parsed = new URL(url);
	parsed.search = '';
	parsed.hash = '';
	return parsed.toString();
}

export async function getBlobAccessUrl(
	url: string,
	userId: string | null | undefined,
): Promise<string> {
	if (!isAzureBlobUrl(url) || !userId) return url;
	return fileUploadService.generateAccessUrlFromFileUrl(
		stripBlobUrlQuery(url),
		userId,
	);
}

// 'loading' while a SAS access URL is being fetched, 'ready' once a usable URL
// is available (or none is needed), 'error' when the fetch rejected. Callers
// that only want the URL can use `useBlobAccessUrl`; callers that must react to
// a failed fetch (e.g. to show a fallback instead of an empty <img>) need the
// status, because a failed fetch and an in-flight fetch both leave `url` null.
export type BlobAccessStatus = 'loading' | 'ready' | 'error';

export function useBlobAccessUrlWithStatus(url: string | null | undefined): {
	url: string | null;
	status: BlobAccessStatus;
} {
	const userId = useUserId();
	const [accessUrl, setAccessUrl] = useState<string | null>(
		isAzureBlobUrl(url) ? null : (url ?? null),
	);
	const [status, setStatus] = useState<BlobAccessStatus>(
		isAzureBlobUrl(url) ? 'loading' : 'ready',
	);

	useEffect(() => {
		let cancelled = false;
		if (!url) {
			setAccessUrl(null);
			setStatus('ready');
			return () => {
				cancelled = true;
			};
		}

		if (!isAzureBlobUrl(url) || !userId) {
			setAccessUrl(url);
			setStatus('ready');
			return () => {
				cancelled = true;
			};
		}

		setAccessUrl(null);
		setStatus('loading');
		getBlobAccessUrl(url, userId)
			.then((resolved) => {
				if (!cancelled) {
					setAccessUrl(resolved);
					setStatus('ready');
				}
			})
			.catch(() => {
				if (!cancelled) {
					setAccessUrl(null);
					setStatus('error');
				}
			});

		return () => {
			cancelled = true;
		};
	}, [url, userId]);

	return { url: accessUrl, status };
}

export function useBlobAccessUrl(
	url: string | null | undefined,
): string | null {
	return useBlobAccessUrlWithStatus(url).url;
}

// The list form of useBlobAccessUrl. It answers undefined until the first slot
// settles, then the list in the same order. Each slot changes to its fresh URL
// when that URL arrives, so one slow request does not hold back the others. A
// slot keeps its stored URL until then, and keeps it if its fresh URL fails. A
// URL that is not in blob storage stays as it is.
export function useBlobAccessUrls(
	urls: string[] | undefined,
): string[] | undefined {
	const userId = useUserId();
	const key = urls?.join('\n');
	const needsFetch = !!userId && !!urls?.some((url) => isAzureBlobUrl(url));
	const [fresh, setFresh] = useState<string[] | undefined>(
		needsFetch ? undefined : urls,
	);

	useEffect(() => {
		let cancelled = false;
		if (!urls || !needsFetch) {
			setFresh(urls);
			return () => {
				cancelled = true;
			};
		}
		setFresh(undefined);
		const resolved = [...urls];
		urls.forEach((url, i) => {
			void getBlobAccessUrl(url, userId)
				.catch(() => url)
				.then((next) => {
					if (cancelled) return;
					resolved[i] = next;
					setFresh([...resolved]);
				});
		});
		return () => {
			cancelled = true;
		};
		// `key` stands for `urls`: bridge state gives a new array on each delta.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [key, needsFetch, userId]);

	return fresh;
}
